import { useMemo, useCallback } from 'react';
import {
  VisibilityState,
  Updater,
  functionalUpdate,
} from '@tanstack/react-table';
import {
  TableName,
  TableSettings,
  useGetPreferences,
  preferenceKeys,
  useUpdatePreferences,
  PreferenceControllerUpsertPreferencesParamsTypeEnum,
  PreferenceDtoTypeEnum,
  TablePreference,
  PreferenceControllerGetPreferencesParamsTypeEnum,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';

export const useTablePreferencesSettings = (tableName: TableName) => {
  const organizationId = useUserSetupStore(state => state.organization.id);

  const { data: organizationData } = useGetPreferences({
    key: preferenceKeys.tableSettings,
    type: PreferenceControllerGetPreferencesParamsTypeEnum.Organization,
    organizationId,
  });

  const { data } = useGetPreferences({
    key: preferenceKeys.tableSettings,
  });

  const { mutate } = useUpdatePreferences();
  const globalTableSettings = data?.items.find(
    item => item.type === PreferenceDtoTypeEnum.Global
  )?.value as TableSettings;
  const userTableSettings = data?.items.find(
    item => item.type === PreferenceDtoTypeEnum.User
  )?.value as TableSettings;
  const organizationTableSettings = organizationData?.items.find(
    item => item.type === PreferenceDtoTypeEnum.Organization
  )?.value as TableSettings;

  const disabledColumns = useMemo<Set<string>>(() => {
    const globalPrefs = globalTableSettings?.[tableName] ?? {};
    const orgPrefs = organizationTableSettings?.[tableName] ?? {};
    const disabled = new Set(
      Object.entries(globalPrefs)
        .filter(
          ([columnId, pref]) =>
            pref.enabled === false && orgPrefs[columnId]?.enabled !== true
        )
        .map(([columnId]) => columnId)
    );

    return disabled;
  }, [globalTableSettings, organizationTableSettings, tableName]);

  const mapPreferences = useCallback(
    (preferences: { [key: string]: TablePreference }) =>
      Object.entries(preferences).map(([key, value]) => [
        key,
        value.visible ?? true,
      ]),
    []
  );

  const globalColumnVisibility = useMemo<VisibilityState>(
    () =>
      Object.fromEntries(
        mapPreferences(globalTableSettings?.[tableName] ?? {})
      ),
    [globalTableSettings, tableName, mapPreferences]
  );

  const organizationColumnVisibility = useMemo<VisibilityState>(
    () =>
      Object.fromEntries(
        mapPreferences(organizationTableSettings?.[tableName] ?? {})
      ),
    [organizationTableSettings, tableName, mapPreferences]
  );

  const userColumnVisibility = useMemo<VisibilityState>(
    () =>
      Object.fromEntries(mapPreferences(userTableSettings?.[tableName] ?? {})),
    [userTableSettings, tableName, mapPreferences]
  );

  const disabledColumnsVisibility = useMemo<VisibilityState>(
    () => Object.fromEntries([...disabledColumns].map(col => [col, false])),
    [disabledColumns]
  );

  const hasLoadedPreferences = !!data && !!organizationData;

  const saveColumnVisibility = useCallback(
    (partial: VisibilityState) => {
      if (!hasLoadedPreferences) return;

      const filteredPartial = Object.fromEntries(
        Object.entries(partial).filter(([key]) => !disabledColumns.has(key))
      );

      const merged: VisibilityState = {
        ...(userColumnVisibility ?? {}),
        ...filteredPartial,
      };

      const mappedTablePreferences = Object.entries(merged).map(
        ([key, value]) => [
          key,
          {
            ...(userTableSettings?.[tableName]?.[key] ?? {}),
            visible: value,
          },
        ]
      );

      const updatedTableSettings: TableSettings = {
        ...(userTableSettings ?? {}),
        [tableName]: {
          ...(userTableSettings?.[tableName] ?? {}),
          ...Object.fromEntries(mappedTablePreferences),
        },
      };

      mutate({
        key: preferenceKeys.tableSettings,
        query: {
          key: preferenceKeys.tableSettings,
          type: PreferenceControllerUpsertPreferencesParamsTypeEnum.User,
        },
        data: { value: updatedTableSettings },
      });
    },
    [
      hasLoadedPreferences,
      userColumnVisibility,
      userTableSettings,
      tableName,
      mutate,
      disabledColumns,
    ]
  );

  const handleColumnVisibilityChange = useCallback(
    (updaterOrValue: Updater<VisibilityState>) => {
      const nextVisibility = functionalUpdate(
        updaterOrValue,
        userColumnVisibility ?? {}
      );
      saveColumnVisibility(nextVisibility);
    },
    [saveColumnVisibility, userColumnVisibility]
  );

  return {
    onColumnVisibilityChange: handleColumnVisibilityChange,
    disabledColumns,
    state: {
      columnVisibility: hasLoadedPreferences
        ? {
            ...globalColumnVisibility,
            ...organizationColumnVisibility,
            ...userColumnVisibility,
            ...disabledColumnsVisibility,
          }
        : null,
    },
  };
};
